/**
 * Login Config Mapper - Central normalization layer for login page configuration
 * 
 * This utility transforms raw API config into a normalized, template-ready format.
 * All templates MUST use the mapped config - no direct config consumption.
 * 
 * Benefits:
 * - Single source of truth for defaults
 * - Pre-computed visibility booleans (no inline lookups in templates)
 * - Type-safe with explicit fields
 * - Backward compatible with existing saved configs
 */

import type {
  LoginTemplateConfig,
  LoginSectionConfig,
  LoginPanelPosition,
  LoginSectionId,
  LoginPanelBackgroundConfig,
  LoginBackgroundType,
  LoginBackgroundPatternType,
  LoginFormGlassConfig,
} from '../types/loginTemplate';
import { colorTokenToCss, gradientFromTokens } from './colorTokens';

// ============================================================================
// TYPES
// ============================================================================

/** Pre-computed visibility flags for a section on both panels */
export interface SectionVisibility {
  visible: boolean;
  showOn: LoginPanelPosition;
  showOnLeft: boolean;
  showOnRight: boolean;
}

/** Resolved styles for a panel */
export interface ResolvedPanelStyle {
  backgroundColor: string;
  textColor: string;
}

/** Resolved styles for buttons */
export interface ResolvedButtonStyle {
  backgroundColor: string;
  textColor: string;
}

/** Resolved background configuration */
export interface ResolvedBackground {
  type: LoginBackgroundType;
  value: string;
  patternEnabled: boolean;
  patternType: LoginBackgroundPatternType;
  patternOpacity: number;
  gradientColor1: string;
  gradientColor2: string;
  gradientDirection: string;
  /** Pre-computed CSS background style */
  cssBackground: string;
}

/** Resolved text colors */
export interface ResolvedColors {
  heading: string;
  subheading: string;
  description: string;
  featureTitle: string;
  featureDescription: string;
  statsLabel: string;
  statsValue: string;
  featureCardBox: string;
  statsContainerBox: string;
  loginFormBox: string;
}

/** Feature card data */
export interface FeatureCard {
  icon: string;
  title: string;
  description: string;
}

/** Statistic data */
export interface StatItem {
  value: string;
  label: string;
}

/** Section-specific content */
export interface SectionContent {
  title?: string;
  subtitle?: string;
  description?: string;
  items?: Array<{ title?: string; description?: string; [key: string]: unknown }>;
}

/**
 * Mapped Login Config - the normalized, template-ready configuration.
 * Templates should ONLY use this interface, never the raw API config.
 */
export interface MappedLoginConfig {
  // Template identification
  templateId: string;
  templateName: string;
  formPlacement: 'left' | 'right' | 'center' | 'overlay';

  // Layout flags
  showLeftPanel: boolean;
  showRightPanel: boolean;

  // Section visibility (pre-computed for each section)
  sections: {
    logo: SectionVisibility;
    title: SectionVisibility;
    welcome: SectionVisibility;
    intro: SectionVisibility;
    featureCards: SectionVisibility;
    stats: SectionVisibility;
    help: SectionVisibility;
    legalLinks: SectionVisibility;
    backgroundVisual: SectionVisibility;
    customInfo: SectionVisibility;
  };

  // Section-specific content (from section.content in config)
  sectionContent: {
    title: SectionContent;
    welcome: SectionContent;
    intro: SectionContent;
    help: SectionContent;
    customInfo: SectionContent;
  };

  // Logo position (separate from logo section visibility)
  logoPosition: LoginPanelPosition;
  showLogoOnLeft: boolean;
  showLogoOnRight: boolean;

  // Background
  background: ResolvedBackground;
  leftPanelBackground: ResolvedBackground;
  rightPanelBackground: ResolvedBackground;

  // Panel styles
  leftPanelStyle: ResolvedPanelStyle;
  rightPanelStyle: ResolvedPanelStyle;

  // Element colors
  colors: ResolvedColors;

  // Button style
  buttonStyle: ResolvedButtonStyle;

  // Login form glass config
  loginFormGlass: LoginFormGlassConfig;

  // Custom content
  logoUrl: string;
  title: string;
  subtitle: string;
  description: string;
  featureCards: FeatureCard[];
  stats: StatItem[];

  // Raw config for any edge cases (avoid using if possible)
  _raw: LoginTemplateConfig;
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

const DEFAULT_FEATURE_CARDS: FeatureCard[] = [
  { icon: 'support', title: 'Comprehensive Support', description: 'End-to-end patient program management' },
  { icon: 'secure', title: 'HIPAA Compliant', description: 'Enterprise-grade security & privacy' },
  { icon: 'patients', title: 'Patient-Centric', description: 'Designed with patients in mind' },
];

const DEFAULT_STATS: StatItem[] = [
  { value: '10K+', label: 'Patients Served' },
  { value: '500+', label: 'Healthcare Providers' },
  { value: '99.9%', label: 'Uptime' },
];

const DEFAULT_SECTION_SHOW_ON: Record<LoginSectionId, LoginPanelPosition> = {
  logo: 'left',
  title: 'left',
  welcome: 'right',
  intro: 'left',
  featureCards: 'left',
  stats: 'left',
  help: 'right',
  legalLinks: 'right',
  backgroundVisual: 'left',
  customInfo: 'left',
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get section config from array, with defaults
 */
function getSectionFromConfig(
  sections: LoginSectionConfig[] | undefined,
  sectionId: LoginSectionId
): SectionVisibility {
  const section = sections?.find((s) => s.id === sectionId);
  const visible = section?.visible ?? true;
  const showOn = section?.showOn || DEFAULT_SECTION_SHOW_ON[sectionId];

  return {
    visible,
    showOn,
    showOnLeft: visible && (showOn === 'left' || showOn === 'both'),
    showOnRight: visible && (showOn === 'right' || showOn === 'both'),
  };
}

/**
 * Compute CSS background string from config
 */
export function computeLoginBackgroundCss(
  type: LoginBackgroundType,
  value: string,
  gradientColor1: string,
  gradientColor2: string,
  gradientDirection: string
): string {
  if (type === 'image' && value) {
    return `url(${value}) center/cover no-repeat`;
  }
  if (type === 'solid' && value) {
    return colorTokenToCss(value, 'background');
  }
  // Default to gradient
  return gradientFromTokens(gradientColor1, gradientColor2, gradientDirection);
}

export function resolveLoginBackground(
  background: LoginPanelBackgroundConfig | undefined,
  fallback: LoginPanelBackgroundConfig
): ResolvedBackground {
  // Use ?? for type to preserve explicit values; || treats null/'' as falsy
  const type = background?.type ?? fallback.type ?? 'gradient';
  const value = background?.value || fallback.value || '';
  const patternEnabled = background?.patternEnabled ?? fallback.patternEnabled ?? true;
  const patternType = background?.patternType || fallback.patternType || 'dots';
  const patternOpacity = background?.patternOpacity ?? fallback.patternOpacity ?? 10;
  const gradientColor1 = background?.gradientColor1 || fallback.gradientColor1 || 'primary';
  const gradientColor2 = background?.gradientColor2 || fallback.gradientColor2 || 'info';
  const gradientDirection = background?.gradientDirection || fallback.gradientDirection || '135deg';

  return {
    type,
    value,
    patternEnabled,
    patternType,
    patternOpacity,
    gradientColor1,
    gradientColor2,
    gradientDirection,
    cssBackground: computeLoginBackgroundCss(type, value, gradientColor1, gradientColor2, gradientDirection),
  };
}

/**
 * Compute logo visibility on a specific panel
 */
function computeLogoVisibility(
  logoPosition: LoginPanelPosition | undefined,
  panel: 'left' | 'right'
): boolean {
  const position = logoPosition || 'left';
  if (position === 'none') return false;
  if (position === 'both') return true;
  return position === panel;
}

// ============================================================================
// MAIN MAPPER FUNCTION
// ============================================================================

/**
 * Maps raw API config to a normalized, template-ready format.
 * 
 * @param config - Raw LoginTemplateConfig from API or fallback
 * @param themeSettings - Optional theme settings for additional fallbacks
 * @returns MappedLoginConfig with all values resolved and pre-computed booleans
 * 
 * @example
 * ```tsx
 * const mappedConfig = mapLoginConfig(apiConfig, themeSettings);
 * 
 * // Use in template:
 * {mappedConfig.sections.featureCards.showOnLeft && (
 *   <FeatureCardsSection cards={mappedConfig.featureCards} />
 * )}
 * ```
 */
export function mapLoginConfig(
  config: LoginTemplateConfig,
  themeSettings?: {
    primaryColor?: string;
    secondaryColor?: string;
    loginPageLogoUrl?: string;
    loginPageTitle?: string;
    loginPageSubtitle?: string;
    loginPageDescription?: string;
    loginFeatureCards?: Array<{ icon?: string; title?: string; description?: string }>;
    loginPageStats?: Array<{ value?: string; label?: string }>;
    loginBackgroundType?: 'gradient' | 'solid' | 'image';
    loginBackgroundValue?: string;
    loginBackgroundPatternEnabled?: boolean;
    [key: string]: unknown;
  }
): MappedLoginConfig {
  // Layout
  const showLeftPanel = config.layout?.showLeftPanel ?? true;
  const showRightPanel = config.layout?.showRightPanel ?? true;

  // Logo position
  const logoPosition = config.logoPosition || 'left';

  // Background
  const defaultBackground: LoginPanelBackgroundConfig = {
    type: config.background?.type || themeSettings?.loginBackgroundType || 'gradient',
    value: config.background?.value || themeSettings?.loginBackgroundValue || '',
    patternEnabled: config.background?.patternEnabled ?? themeSettings?.loginBackgroundPatternEnabled ?? true,
    patternType: config.background?.patternType || 'dots',
    patternOpacity: config.background?.patternOpacity ?? 10,
    gradientColor1: config.background?.gradientColor1 || themeSettings?.primaryColor || 'primary',
    gradientColor2: config.background?.gradientColor2 || themeSettings?.secondaryColor || 'info',
    gradientDirection: config.background?.gradientDirection || '135deg',
  };

  // Static safe fallback — used when an explicit panel background exists but has empty fields.
  // This prevents one panel's saved gradient values from leaking into the other panel.
  const staticFallback: LoginPanelBackgroundConfig = {
    type: 'gradient',
    value: '',
    patternEnabled: true,
    patternType: 'dots',
    patternOpacity: 10,
    gradientColor1: 'primary',
    gradientColor2: 'info',
    gradientDirection: '135deg',
  };

  // Left panel: if explicit background config is saved, use static fallback only (no contamination from top-level)
  const leftPanelBackground = resolveLoginBackground(
    config.styles?.leftPanel?.background,
    config.styles?.leftPanel?.background
      ? staticFallback
      : config.styles?.leftPanel?.backgroundColor
        ? { type: 'solid', value: config.styles.leftPanel.backgroundColor, patternEnabled: false }
        : defaultBackground
  );

  // Right panel: if explicit background config is saved, use static fallback only (no contamination from top-level)
  const rightPanelBackground = resolveLoginBackground(
    config.styles?.rightPanel?.background,
    config.styles?.rightPanel?.background
      ? staticFallback
      : config.styles?.rightPanel?.backgroundColor
        ? { type: 'solid', value: config.styles.rightPanel.backgroundColor, patternEnabled: false }
        : defaultBackground
  );

  // Panel styles
  const leftPanelStyle: ResolvedPanelStyle = {
    backgroundColor: config.styles?.leftPanel?.backgroundColor
      ? colorTokenToCss(config.styles.leftPanel.backgroundColor, 'background')
      : '',
    textColor: config.styles?.leftPanel?.textColor
      ? colorTokenToCss(config.styles.leftPanel.textColor, 'textPrimary')
      : '',
  };

  const rightPanelStyle: ResolvedPanelStyle = {
    backgroundColor: config.styles?.rightPanel?.backgroundColor
      ? colorTokenToCss(config.styles.rightPanel.backgroundColor, 'background')
      : '',
    textColor: config.styles?.rightPanel?.textColor
      ? colorTokenToCss(config.styles.rightPanel.textColor, 'textPrimary')
      : '',
  };

  // Button style
  const buttonStyle: ResolvedButtonStyle = {
    backgroundColor: config.styles?.button?.backgroundColor
      ? colorTokenToCss(config.styles.button.backgroundColor, 'primary')
      : '',
    textColor: config.styles?.button?.textColor
      ? colorTokenToCss(config.styles.button.textColor, 'card')
      : '',
  };

  // Colors
  const colors: ResolvedColors = {
    heading: colorTokenToCss(config.colors?.heading, 'card'),
    subheading: colorTokenToCss(config.colors?.subheading, 'textMuted'),
    description: colorTokenToCss(config.colors?.description, 'textMuted'),
    featureTitle: colorTokenToCss(config.colors?.featureTitle, 'card'),
    featureDescription: colorTokenToCss(config.colors?.featureDescription, 'textMuted'),
    statsLabel: colorTokenToCss(config.colors?.statsLabel, 'textMuted'),
    statsValue: colorTokenToCss(config.colors?.statsValue, 'card'),
    featureCardBox: colorTokenToCss(config.colors?.featureCardBox, 'surfaceBase'),
    statsContainerBox: colorTokenToCss(config.colors?.statsContainerBox, 'surfaceBase'),
    loginFormBox: colorTokenToCss(config.colors?.loginFormBox, 'card'),
  };

  // Feature cards - prioritize config > themeSettings > defaults
  let featureCards: FeatureCard[] = DEFAULT_FEATURE_CARDS;
  if (config.customFeatureCards?.length) {
    featureCards = config.customFeatureCards;
  } else if (themeSettings?.loginFeatureCards?.length) {
    featureCards = themeSettings.loginFeatureCards.map((card) => ({
      icon: card.icon || 'support',
      title: card.title || '',
      description: card.description || '',
    }));
  }

  // Stats - prioritize config > themeSettings > defaults
  let stats: StatItem[] = DEFAULT_STATS;
  if (config.customStats?.length) {
    stats = config.customStats;
  } else if (themeSettings?.loginPageStats?.length) {
    stats = themeSettings.loginPageStats.map((stat) => ({
      value: stat.value || '',
      label: stat.label || '',
    }));
  }

  // Content
  const logoUrl = config.customLogoUrl || themeSettings?.loginPageLogoUrl || '';
  const title = config.customTitle || themeSettings?.loginPageTitle || 'EPPREM';
  const subtitle = config.customSubtitle || themeSettings?.loginPageSubtitle || 'Enhanced Patient\nSupport Program';
  const description =
    config.customDescription ||
    themeSettings?.loginPageDescription ||
    'Empowering healthcare teams to deliver personalized patient care with streamlined program management and real-time insights.';

  // Extract section-specific content
  const titleSection = config.sections?.find((s) => s.id === 'title');
  const welcomeSection = config.sections?.find((s) => s.id === 'welcome');
  const introSection = config.sections?.find((s) => s.id === 'intro');
  const helpSection = config.sections?.find((s) => s.id === 'help');
  const customInfoSection = config.sections?.find((s) => s.id === 'customInfo');

  const sectionContent: MappedLoginConfig['sectionContent'] = {
    title: {
      title: titleSection?.content?.title || '',
      subtitle: titleSection?.content?.subtitle || subtitle,
      description: titleSection?.content?.description || '',
    },
    welcome: {
      title: welcomeSection?.content?.title || 'Welcome to EPPREM',
      subtitle: welcomeSection?.content?.subtitle || 'Sign in to access your patient support dashboard',
      description: welcomeSection?.content?.description || '',
    },
    intro: {
      title: introSection?.content?.title || '',
      subtitle: introSection?.content?.subtitle || '',
      description: introSection?.content?.description || description,
    },
    help: {
      title: helpSection?.content?.title || '',
      subtitle: helpSection?.content?.subtitle || '',
      description: helpSection?.content?.description || '',
    },
    customInfo: {
      title: customInfoSection?.content?.title || '',
      subtitle: customInfoSection?.content?.subtitle || '',
      description: customInfoSection?.content?.description || '',
      items: customInfoSection?.content?.items || [],
    },
  };

  return {
    // Template identification
    templateId: config.templateId,
    templateName: config.templateName || '',
    formPlacement: config.formPlacement || 'right',

    // Layout flags
    showLeftPanel,
    showRightPanel,

    // Section visibility
    sections: {
      logo: getSectionFromConfig(config.sections, 'logo'),
      title: getSectionFromConfig(config.sections, 'title'),
      welcome: getSectionFromConfig(config.sections, 'welcome'),
      intro: getSectionFromConfig(config.sections, 'intro'),
      featureCards: getSectionFromConfig(config.sections, 'featureCards'),
      stats: getSectionFromConfig(config.sections, 'stats'),
      help: getSectionFromConfig(config.sections, 'help'),
      legalLinks: getSectionFromConfig(config.sections, 'legalLinks'),
      backgroundVisual: getSectionFromConfig(config.sections, 'backgroundVisual'),
      customInfo: getSectionFromConfig(config.sections, 'customInfo'),
    },

    // Section-specific content (from section.content in config)
    sectionContent,

    // Logo position
    logoPosition,
    showLogoOnLeft: computeLogoVisibility(logoPosition, 'left'),
    showLogoOnRight: computeLogoVisibility(logoPosition, 'right'),

    // Background
    background: leftPanelBackground,
    leftPanelBackground,
    rightPanelBackground,

    // Panel styles
    leftPanelStyle,
    rightPanelStyle,

    // Colors
    colors,

    // Button style
    buttonStyle,

    // Login form glass config
    loginFormGlass: config.styles?.loginFormGlass || {},

    // Custom content
    logoUrl,
    title,
    subtitle,
    description,
    featureCards,
    stats,

    // Raw config for edge cases
    _raw: config,
  };
}

/**
 * Normalizes config before saving to ensure no undefined values.
 * Call this in the settings page before sending to API.
 */
export function normalizeConfigForSave(
  config: Partial<LoginTemplateConfig>
): LoginTemplateConfig {
  const sections: LoginSectionConfig[] = (config.sections || []).map((section, index) => ({
    id: section.id,
    visible: section.visible ?? true,
    order: section.order ?? index + 1,
    showOn: section.showOn || DEFAULT_SECTION_SHOW_ON[section.id as LoginSectionId] || 'left',
  }));

  // Ensure all section IDs are present
  const existingIds = new Set(sections.map((s) => s.id));
  const allSectionIds: LoginSectionId[] = [
    'logo',
    'welcome',
    'intro',
    'featureCards',
    'stats',
    'help',
    'legalLinks',
    'backgroundVisual',
  ];

  allSectionIds.forEach((id, index) => {
    if (!existingIds.has(id)) {
      sections.push({
        id,
        visible: true,
        order: sections.length + index + 1,
        showOn: DEFAULT_SECTION_SHOW_ON[id],
      });
    }
  });

  return {
    templateId: config.templateId || 'classic',
    templateName: config.templateName || '',
    description: config.description || '',
    formPlacement: config.formPlacement || 'right',
    sections,
    layout: {
      showLeftPanel: config.layout?.showLeftPanel ?? true,
      showRightPanel: config.layout?.showRightPanel ?? true,
    },
    logoPosition: config.logoPosition || 'left',
    background: {
      type: config.background?.type || 'gradient',
      value: config.background?.value || '',
      patternEnabled: config.background?.patternEnabled ?? true,
      patternType: config.background?.patternType || 'dots',
      patternOpacity: config.background?.patternOpacity ?? 10,
      gradientColor1: config.background?.gradientColor1 || 'primary',
      gradientColor2: config.background?.gradientColor2 || 'info',
      gradientDirection: config.background?.gradientDirection || '135deg',
    },
    colors: {
      heading: config.colors?.heading || '',
      subheading: config.colors?.subheading || '',
      description: config.colors?.description || '',
      featureTitle: config.colors?.featureTitle || '',
      featureDescription: config.colors?.featureDescription || '',
      statsLabel: config.colors?.statsLabel || '',
      statsValue: config.colors?.statsValue || '',
      featureCardBox: config.colors?.featureCardBox || '',
      statsContainerBox: config.colors?.statsContainerBox || '',
      loginFormBox: config.colors?.loginFormBox || '',
    },
    styles: {
      leftPanel: {
        backgroundColor: config.styles?.leftPanel?.backgroundColor || '',
        textColor: config.styles?.leftPanel?.textColor || '',
        background: config.styles?.leftPanel?.background,
      },
      rightPanel: {
        backgroundColor: config.styles?.rightPanel?.backgroundColor || '',
        textColor: config.styles?.rightPanel?.textColor || '',
        background: config.styles?.rightPanel?.background,
      },
      button: {
        backgroundColor: config.styles?.button?.backgroundColor || '',
        textColor: config.styles?.button?.textColor || '',
      },
      loginFormGlass: config.styles?.loginFormGlass || {},
    },
    customLogoUrl: config.customLogoUrl || '',
    customTitle: config.customTitle || '',
    customSubtitle: config.customSubtitle || '',
    customDescription: config.customDescription || '',
    customFeatureCards: config.customFeatureCards || [],
    customStats: config.customStats || [],
  };
}

/**
 * Creates a default config for when no API config exists.
 * Used as fallback in LoginPage.tsx
 */
export function getDefaultLoginConfig(): LoginTemplateConfig {
  return normalizeConfigForSave({
    templateId: 'classic',
    templateName: 'Classic Split',
    formPlacement: 'right',
  });
}
